import { defaultDateFieldOption } from '../components/layout/training-matrix/display-options'
import type { DropdownOption } from '../components/UI/multi-select-dropdown/MultiSelectDropdown'
import { filtersActions, trainingMatrixActions } from '../redux/actions'
import { useAppDispatch } from '../redux/hooks'
import type { TMViewFilters } from '../redux/slices/training-matrix-filters-slice'
import { dateFieldOptions } from '../redux/types/data-options'
import type { ExtendedDropdownResult } from '../redux/types/hierarchy-values'
import { useTrainingMatrixViews } from './useTrainingMatrixViews'

export const useTMViewsManagement = () => {
  const dispatch = useAppDispatch()

  const { views } = useTrainingMatrixViews()

  const handleRemoveSelectedTMView = () => {
    if (document.documentElement.classList.contains('colour-blind')) {
      document.documentElement.classList.toggle('colour-blind')
    }

    dispatch(
      filtersActions.setViewFilters({
        competencies: [],
        people: [],
        managers: [],
        occupations: [],
        hierarchies: [],
        hierarchyViewMode: { text: 'All', value: 'all' },
        applyHierarchyFiltersToOccupationRequirements: false,
        showOtherCompetencies: false,
        showAllReports: false,
        showDates: false,
        exportFormat: 'ICONS',
        selectedDisplayDate: defaultDateFieldOption,
        gridlinesFrequency: 5,
        showGridSeparator: true,
        showGroups: false,
        showDesiredCompetencies: false,
        expirationDateLimit: 30,
        slantHeaders: true,
        showOverallCompliancePercentage: false,
        showPercentageOfComplianceForCompetencies: false,
        showPercentageOfComplianceForIndividuals: false,
        showPercentageOfComplianceForGroups: false,
        showManagersColumn: false,
        isColourBlindThemeEnabled: false,
        columnsToShow: []
      } satisfies TMViewFilters)
    )

    dispatch(trainingMatrixActions.setSelectedView(undefined))
  }

  const handleSelectTMView = (selection?: DropdownOption) => {
    if (!selection) {
      handleRemoveSelectedTMView()
      return
    } else {
      // step 1: set the selected view
      dispatch(trainingMatrixActions.setSelectedView(selection))

      const selectedView = views?.find(view => view.id === selection.value)
      // Load filters
      const selectedViewFilters = selectedView?.filters || {}
      // Load settings
      const selectedViewDisplaySettings = selectedView?.settings

      // Managers computation
      const viewManagers = views?.map(view => {
        return { title: view?.title ?? '', managers: view?.displayMetadata?.managers || [] }
      })
      const managers = viewManagers?.find(viewPair => viewPair.title === selection.text)?.managers || []

      if (
        selectedViewDisplaySettings?.useColorBlindPalette &&
        !document.documentElement.classList.contains('colour-blind')
      ) {
        document.documentElement.classList.toggle('colour-blind')
      } else if (
        !selectedViewDisplaySettings?.useColorBlindPalette &&
        document.documentElement.classList.contains('colour-blind')
      ) {
        document.documentElement.classList.toggle('colour-blind')
      }

      const selectedColumns = selectedViewDisplaySettings?.selectedColumns?.map(column => {
        return { text: column, value: column } as ExtendedDropdownResult
      })

      const selectedDisplayDate =
        dateFieldOptions.find(option => option.exportFormat === selectedViewDisplaySettings?.exportFormat) ??
        defaultDateFieldOption

      // step 3: dispatches for filter updates as well
      dispatch(
        filtersActions.setViewFilters({
          competencies:
            selectedViewFilters.competencies?.map(c => {
              return { text: c.name ?? '', value: c.id ?? '' }
            }) ?? [],
          people:
            selectedViewFilters.people?.map(p => {
              return { text: p.fullName ?? '', value: p.id ?? '' }
            }) ?? [],
          managers: managers?.map(manager => {
            return {
              value: manager.id ?? '',
              text: manager.fullName ?? ''
            }
          }),
          occupations:
            selectedViewFilters.occupations?.map(o => {
              return {
                value: o,
                text: o
              }
            }) ?? [],
          hierarchies: selectedViewFilters.hierarchies ?? [],
          hierarchyViewMode: {
            text: selectedViewFilters.matchOption === 'MATCH_ALL' ? 'All' : 'Any',
            value: selectedViewFilters.matchOption === 'MATCH_ALL' ? 'all' : 'any'
          },
          showOtherCompetencies: selectedViewFilters.showAllCompetencies ?? false,
          showAllReports: selectedViewFilters.showAllReports ?? false,
          showDates: selectedViewDisplaySettings?.showDates ?? false,
          exportFormat: selectedViewDisplaySettings?.exportFormat ?? 'ICONS',
          selectedDisplayDate: selectedDisplayDate,
          gridlinesFrequency: selectedViewDisplaySettings?.gridlineFrequency ?? 5,
          showGridSeparator: selectedViewDisplaySettings?.showGridlines ?? true,
          showGroups: selectedViewDisplaySettings?.showCompetencyGroups ?? false,
          showDesiredCompetencies: selectedViewDisplaySettings?.showDesiredCompetencies ?? false,
          expirationDateLimit: selectedViewDisplaySettings?.expirationDateLimit ?? 30,
          slantHeaders: selectedViewDisplaySettings?.angleCompetencyHeaders ?? true,
          showOverallCompliancePercentage: selectedViewDisplaySettings?.showOverallCompliancePercentage ?? false,
          showPercentageOfComplianceForCompetencies:
            selectedViewDisplaySettings?.showPercentageOfComplianceForCompetencies ?? false,
          showPercentageOfComplianceForIndividuals:
            selectedViewDisplaySettings?.showPercentageOfComplianceForIndividuals ?? false,
          showPercentageOfComplianceForGroups:
            selectedViewDisplaySettings?.showPercentageOfComplianceForCompetencyGroups ?? false,
          showManagersColumn: selectedViewDisplaySettings?.isManagerColumnSelected ?? false,
          isColourBlindThemeEnabled: selectedViewDisplaySettings?.useColorBlindPalette ?? false,
          columnsToShow: selectedColumns ?? [],
          applyHierarchyFiltersToOccupationRequirements:
            selectedViewFilters.applyHierarchyFiltersToOccupationRequirements ?? false
        } satisfies TMViewFilters)
      )
    }
  }

  return {
    handleRemoveSelectedTMView: handleRemoveSelectedTMView,
    handleSelectTMView: handleSelectTMView
  }
}
